/* ============================================================
   AQUA GOLD — PREMIUM JS
   Sticky mobile ATC + scroll fade-ins. Pure vanilla, no deps.
   Runs only when features are applicable. Safe on all templates.
   ============================================================ */

(function () {
  'use strict';

  // ---- Helpers ----
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
  const isProductPage = () =>
    document.body.classList.contains('template-product') ||
    document.body.getAttribute('data-template') === 'product' ||
    !!document.querySelector('.template-product');

  // =========================================================
  // 1. STICKY MOBILE ADD-TO-CART BAR — DISABLED (2026-04-17)
  // The Aurora theme ships its own sticky cart bar via
  // snippets/sticky-cart.liquid. Our custom ag-mobile-atc was
  // stacking on top and caused the confusing "options disappear"
  // scroll behavior reported by the operator. Short-circuited.
  // =========================================================
  function initStickyATC() {
    return; // no-op — theme handles sticky ATC natively
    // eslint-disable-next-line no-unreachable
    if (!isProductPage() || !isMobile()) return;

    // Find the original ATC form/button — try common selectors
    const originalBtn =
      document.querySelector('form[action*="/cart/add"] button[name="add"]') ||
      document.querySelector('.product-form__cart-submit') ||
      document.querySelector('button.btn-theme[name="add"]');

    if (!originalBtn) return;

    const form = originalBtn.closest('form');
    if (!form) return;

    // Pull product title + price from the page
    const titleEl =
      document.querySelector('.product-single__title') ||
      document.querySelector('.title-product') ||
      document.querySelector('h1');

    const priceEl =
      document.querySelector('.product-single__price') ||
      document.querySelector('.price-product') ||
      document.querySelector('.price');

    const title = titleEl ? titleEl.textContent.trim() : '';
    const price = priceEl ? priceEl.textContent.trim() : '';

    // Build the bar
    const bar = document.createElement('div');
    bar.className = 'ag-mobile-atc';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = `
      <div class="ag-mobile-atc__info">
        <p class="ag-mobile-atc__title">${title}</p>
        <p class="ag-mobile-atc__price">${price}</p>
      </div>
      <button type="button" class="ag-mobile-atc__button" aria-label="Add to cart">
        Add to Cart
      </button>
    `;
    document.body.appendChild(bar);

    const barBtn = bar.querySelector('.ag-mobile-atc__button');

    // Sync disabled state with original button
    const syncDisabled = () => {
      const disabled = originalBtn.disabled || originalBtn.classList.contains('btn--sold-out');
      barBtn.disabled = disabled;
      barBtn.textContent = disabled ? 'Sold Out' : 'Add to Cart';
    };
    syncDisabled();

    // Sync price/title when variant changes (theme fires change on form)
    const syncPrice = () => {
      const p =
        document.querySelector('.product-single__price') ||
        document.querySelector('.price-product') ||
        document.querySelector('.price');
      if (p) {
        bar.querySelector('.ag-mobile-atc__price').textContent = p.textContent.trim();
      }
      syncDisabled();
    };

    // Observe disabled attribute + class changes on the original button
    const obs = new MutationObserver(syncPrice);
    obs.observe(originalBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });

    // Also hook form change (variant change)
    form.addEventListener('change', () => setTimeout(syncPrice, 120));

    // Click handler — submit the original form
    barBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (barBtn.disabled) return;

      // Haptic feedback if available
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(8);
      }

      // Click the real submit button to trigger theme's cart logic
      originalBtn.click();
    });

    // Show/hide logic: visible when original ATC is off-screen
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            bar.classList.remove('is-visible');
            bar.setAttribute('aria-hidden', 'true');
          } else {
            // Only show after some scroll (avoid flash on load if above fold)
            if (window.scrollY > 200) {
              bar.classList.add('is-visible');
              bar.setAttribute('aria-hidden', 'false');
            }
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );

    io.observe(originalBtn);
  }

  // =========================================================
  // 2. SCROLL FADE-IN (subtle luxury reveal)
  // =========================================================
  function initFadeIns() {
    // Only if IntersectionObserver is supported & not reduced motion
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Auto-tag common section wrappers.
    // NOTE: On product pages we deliberately DO NOT fade the main row
    // (.row.product-single) because it contains the variant selectors
    // and sticky gallery — animating its opacity/transform on scroll
    // caused visible option glitches (2026-04-17 fix).
    const selectors = [
      '.template-index .shopify-section',
      '.template-collection .shopify-section',
      '.template-product .shopify-section + .shopify-section' // downstream sections only
    ];

    const seen = new WeakSet();
    document.querySelectorAll(selectors.join(',')).forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      // Never animate: hero, header, footer, or anything wrapping the
      // product form (variant options live there).
      if (el.classList.contains('slideshow-1') || el.classList.contains('header')) return;
      if (el.querySelector && (
            el.querySelector('form[action*="/cart/add"]') ||
            el.querySelector('.product-single') ||
            el.querySelector('.selector-wrapper') ||
            el.classList.contains('site-header') ||
            el.classList.contains('site-footer')
         )) return;
      // AOS 2026-09-16 — never animate a section that is already in the initial
      // viewport. Painting it, hiding it, then fading it back in re-triggers
      // LCP: Lighthouse measured the collection-page description at 15s for
      // exactly this reason. Only sections the user scrolls to should reveal.
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('ag-fade-up');
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.ag-fade-up').forEach((el) => io.observe(el));
  }

  // =========================================================
  // 3. IMG LAZY LOADING PROGRESSIVE ENHANCEMENT
  // =========================================================
  function initLazyLoaded() {
    // Mark images as loaded so shimmer bg is removed
    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
      }
    });
  }

  // =========================================================
  // 4. SMOOTH QUANTITY STEPPER ENHANCEMENT
  // For themes that don't have qty +/- buttons, inject them.
  // =========================================================
  function initQtyStepper() {
    if (!isProductPage()) return;

    const qtyWrap = document.querySelector('.product-form__item--quantity');
    const qtyInput = document.querySelector('.product-form__quantity');
    if (!qtyWrap || !qtyInput) return;
    if (qtyWrap.querySelector('.qty-btn')) return; // already has one

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'qty-btn qty-btn--minus';
    minus.setAttribute('aria-label', 'Decrease quantity');
    minus.textContent = '−';

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'qty-btn qty-btn--plus';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.textContent = '+';

    qtyWrap.insertBefore(minus, qtyInput);
    qtyWrap.appendChild(plus);

    minus.addEventListener('click', () => {
      const v = parseInt(qtyInput.value, 10) || 1;
      if (v > 1) {
        qtyInput.value = v - 1;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    plus.addEventListener('click', () => {
      const v = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = v + 1;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // =========================================================
  // 5. STICKY-CART REPAIR (2026-04-17)
  // The theme ships a sticky-cart bar (snippets/sticky-cart.liquid) that
  // keeps title + variant selectors + price + ATC pinned to the bottom of
  // the viewport once the main ATC scrolls off-screen. Without it, there's
  // a scroll window where options disappear but ATC + Buy Now remain
  // clickable — customers can add to cart without seeing their variant.
  //
  // Two problems we fix here:
  //   a) theme.js persists `localDisableStCart=true` in localStorage when
  //      users click the bar's close button — permanently kills the bar.
  //   b) the activation anchor #js-anchor-sticky-cart sits at the very
  //      bottom of the product section, so the bar only triggers after
  //      the user has already scrolled past everything.
  //
  // Fix: clear the flag, remove the close control, and relocate the anchor
  // to just after the ATC area so the bar appears the moment options
  // scroll out of view.
  // =========================================================
  function initStickyCartRepair() {
    if (!isProductPage()) return;

    // (a0) Kill SVG <style>/<defs> text bleed. The theme inlines salepoint
    // and trust-badge icons with <defs><style>.a{...}</style></defs> inside
    // the SVG. Something in the stack (probably Shopify's SVG sanitizer or
    // a CSS reset) is rendering the <style> content as visible text — so
    // shoppers see ".a{fill:none;stroke:#000;stroke-width:2px}" above their
    // "Fast Tracked USA Shipping" copy. Force-hide.
    if (!document.getElementById('ag-style-bleed-fix')) {
      const s = document.createElement('style');
      s.id = 'ag-style-bleed-fix';
      s.textContent =
        '.template-product .salepoint style,' +
        '.template-product .salepoint defs,' +
        '.template-product .ptrust-badge style,' +
        '.template-product .ptrust-badge defs,' +
        '.template-product .product-block style,' +
        '.template-product .product-block defs{display:none!important;}';
      document.head.appendChild(s);
    }

    // (a) Clear the "permanently disabled" flag so the bar can show.
    try { localStorage.removeItem('localDisableStCart'); } catch (_) {}

    const bar = document.querySelector('.sticky-cart-wr');
    if (bar) bar.classList.remove('disable');

    // (b) Remove the close control entirely — we don't want users to
    // re-disable the bar. theme.js also re-toggles `disable` on click,
    // so removing the node severs that path.
    const closeBtn = document.getElementById('js-sticky-close');
    if (closeBtn && closeBtn.parentNode) {
      closeBtn.parentNode.removeChild(closeBtn);
    }

    // (c) Move the activation anchor to sit right after the ATC area.
    // theme.stickyCart calls $anchor.offset() live on every scroll event,
    // so moving the DOM node re-points the trigger without re-init.
    const anchor = document.getElementById('js-anchor-sticky-cart');
    const atcBtn = document.querySelector(
      'form[action*="/cart/add"] button[name="add"], .product-form__cart-submit'
    );
    const atcArea =
      (atcBtn && atcBtn.closest('.product-form__quantity-submit')) ||
      (atcBtn && atcBtn.closest('.product-button-variant')) ||
      (atcBtn && atcBtn.closest('form'));
    if (anchor && atcArea && atcArea.parentNode) {
      atcArea.parentNode.insertBefore(anchor, atcArea.nextSibling);
      // Kick the scroll handler once so the bar's state reconciles to its
      // new trigger point immediately (no wait for next scroll event).
      window.dispatchEvent(new Event('scroll'));
    }
  }

  // =========================================================
  /* ====== Sticky-cart custom scroll handler v3 ======
   * The theme's jQuery-based `theme.stickyCart` scroll handler is flaky — it
   * captures the anchor at script-parse time and can miss DOM changes or
   * fail to attach if timing is off. Owning the logic here makes the bar
   * reliably show whenever the anchor has scrolled above viewport top.
   */
  function initStickyCartCustomHandler() {
    if (!isProductPage()) return;
    const bar = document.querySelector('.sticky-cart-wr');
    const anchor = document.getElementById('js-anchor-sticky-cart');
    if (!bar || !anchor) return;

    let ticking = false;
    function update() {
      ticking = false;
      const rect = anchor.getBoundingClientRect();
      // Activate the moment the anchor's bottom edge leaves the top of
      // the viewport (small buffer so the bar appears just as main ATC
      // scrolls off, not a beat late).
      if (rect.bottom < 80) {
        bar.classList.add('active');
      } else {
        bar.classList.remove('active');
      }
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Reconcile immediately — handles pages where the user is already
    // scrolled past the ATC on load (back-forward cache, anchor jumps).
    update();
  }
  /* ====== end v3 sticky ====== */

  
  /* ====== Homepage Reactive v6 JS — tilt + magnetic + cursor + stagger + split text ======
   * Hooks into the DOM after load, attaches:
   *  - 3D tilt on .product-card (mousemove rotates card based on pointer position)
   *  - Magnetic attraction on primary CTAs (.btn-primary, shop-now buttons)
   *  - Custom cursor (gold ring + dot, grows on interactive hover)
   *  - IntersectionObserver stagger fade-in for carousel/grid containers
   *  - Word-split reveal for homepage section headings
   * All gated on desktop + hover + no prefers-reduced-motion where applicable.
   */
  function initHomepageReactive() {
    const isHomepage = document.body.classList.contains('template-index');
    if (!isHomepage) return;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    // ===== 3D tilt on product cards =====
    if (canHover) {
      document.querySelectorAll('.product-card').forEach(function(card) {
        let rect = null;
        let raf = null;
        function onMove(e) {
          if (!rect) rect = card.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          const rotY = (x - 0.5) * 10;   // -5deg..5deg horizontal
          const rotX = -(y - 0.5) * 8;   // -4deg..4deg vertical
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function() {
            card.style.setProperty('--tilt-x', rotX.toFixed(2) + 'deg');
            card.style.setProperty('--tilt-y', rotY.toFixed(2) + 'deg');
            card.style.setProperty('--lift', '-6px');
          });
        }
        function onEnter() { rect = card.getBoundingClientRect(); card.classList.add('ag-tilt-active'); }
        function onLeave() {
          card.classList.remove('ag-tilt-active');
          card.style.removeProperty('--tilt-x');
          card.style.removeProperty('--tilt-y');
          card.style.removeProperty('--lift');
        }
        card.addEventListener('mousemove', onMove, { passive: true });
        card.addEventListener('mouseenter', onEnter, { passive: true });
        card.addEventListener('mouseleave', onLeave, { passive: true });
      });
    }

    // ===== Magnetic buttons =====
    if (canHover) {
      const selectors = [
        '.template-index a.btn-primary',
        '.template-index button.btn-primary',
        '.template-index .btn.btn-primary',
        '.template-index [class*="button--primary"]',
        '.template-index .shop-now',
        '.template-index .btn-shop-now',
        '.template-index .slideshow__slide .btn',
        '.template-index .hero__btn'
      ].join(',');
      document.querySelectorAll(selectors).forEach(function(btn) {
        btn.classList.add('ag-magnetic');
        let raf = null;
        btn.addEventListener('mousemove', function(e) {
          const rect = btn.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const dx = (e.clientX - cx) * 0.25;
          const dy = (e.clientY - cy) * 0.25;
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function() {
            btn.style.setProperty('--mag-x', dx.toFixed(1) + 'px');
            btn.style.setProperty('--mag-y', dy.toFixed(1) + 'px');
            btn.classList.add('ag-magnetic-active');
          });
        }, { passive: true });
        btn.addEventListener('mouseleave', function() {
          btn.classList.remove('ag-magnetic-active');
          btn.style.removeProperty('--mag-x');
          btn.style.removeProperty('--mag-y');
        }, { passive: true });
      });
    }

    // ===== Word-split heading reveal =====
    const headings = document.querySelectorAll(
      '.template-index main > .shopify-section:not([id*="hero"]):not([id*="slideshow"]) h2:not(.slideshow__title):not(.mega-title), ' +
      '.template-index main > .shopify-section:not([id*="hero"]):not([id*="slideshow"]) h3'
    );
    headings.forEach(function(h) {
      if (h.dataset.agSplit) return;
      if (h.closest('.product-card')) return; // don't touch card titles
      const rawText = h.innerText.trim();
      if (!rawText) return; // skip empty headings (theme may render text later)
      if (h.closest('[id*="hero"], [id*="slideshow"], .slideshow-section')) return;
      if (h.matches('.slideshow__title, .mega-title, [class*="slideshow"]')) return;
      const words = rawText.split(/\s+/);
      if (words.length === 0 || words.length > 12) return;
      h.innerHTML = words.map(function(w, i) {
        return '<span class="ag-split-word" style="transition-delay:' + (i * 60) + 'ms">' + w + '</span>';
      }).join(' ');
      h.dataset.agSplit = '1';
    });

    // ===== IntersectionObserver for split-reveal + stagger + ag-fade-up =====
    const io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (el.dataset.agSplit === '1') el.classList.add('ag-split-reveal');
        if (el.classList.contains('ag-stagger')) el.classList.add('ag-stagger-in');
        if (el.classList.contains('ag-fade-up')) el.classList.add('ag-in-view');
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '-40px 0px' });
    headings.forEach(function(h) { if (h.dataset.agSplit) io.observe(h); });
    // Apply stagger to product carousels / grids
    document.querySelectorAll(
      '.template-index .slickCarousel, ' +
      '.template-index .slick-track, ' +
      '.template-index [class*="featured-collections"] .row, ' +
      '.template-index [class*="featured-products"] .row'
    ).forEach(function(grid) {
      grid.classList.add('ag-stagger');
      io.observe(grid);
    });
    document.querySelectorAll('.template-index .ag-fade-up').forEach(function(el) { io.observe(el); });
  }
  /* ====== end v6 JS ====== */

  
  /* ====== Reactive v7 JS — spotlight + parallax + blur + stats ====== */
  function initReactiveV7() {
    if (!document.body.classList.contains('template-index')) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // 1) Spotlight cursor glow
    if (canHover && !reducedMotion) {
      let mx = 0, my = 0, rafId = null;
      function update() {
        document.body.style.setProperty('--mx', mx + 'px');
        document.body.style.setProperty('--my', my + 'px');
        rafId = null;
      }
      window.addEventListener('mousemove', function(e) {
        mx = e.clientX; my = e.clientY;
        if (!rafId) rafId = requestAnimationFrame(update);
      }, { passive: true });
    }

    // 2) Mouse-parallax hero
    if (canHover && !reducedMotion) {
      const heroContainers = document.querySelectorAll(
        '.template-index [class*="slideshow"] .slideshow__slide, ' +
        '.template-index [class*="slideshow-section"] .slideshow__slide, ' +
        '.template-index .hero, .template-index [class*="hero"]'
      );
      heroContainers.forEach(function(hero) {
        // Mark its immediate child image/text layers for parallax
        const layers = hero.querySelectorAll('img, picture, [class*="slideshow__text"], [class*="heading"], [class*="hero__content"]');
        layers.forEach(function(l) { l.classList.add('ag-parallax-layer'); });
        let raf = null;
        hero.addEventListener('mousemove', function(e) {
          const rect = hero.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width - 0.5) * 2;   // -1..1
          const py = ((e.clientY - rect.top) / rect.height - 0.5) * 2;   // -1..1
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function() {
            hero.style.setProperty('--px', px.toFixed(3));
            hero.style.setProperty('--py', py.toFixed(3));
            hero.classList.add('ag-parallax-active');
          });
        }, { passive: true });
        hero.addEventListener('mouseleave', function() {
          hero.classList.remove('ag-parallax-active');
          hero.style.removeProperty('--px');
          hero.style.removeProperty('--py');
        }, { passive: true });
      });
    }

    // 3) Progressive blur on scroll (blur sections that scrolled past top)
    if (!reducedMotion) {
      const sections = document.querySelectorAll('.template-index main > .shopify-section');
      const io = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          const el = entry.target;
          if (entry.intersectionRatio < 0.15 && entry.boundingClientRect.top < 0) {
            el.classList.add('ag-blur-past');
          } else {
            el.classList.remove('ag-blur-past');
          }
        });
      }, { threshold: [0, 0.15, 0.5, 1], rootMargin: '0px 0px -30% 0px' });
      sections.forEach(function(s) { io.observe(s); });
    }

    // 4) Inject animated stat counters after hero
    (function injectStats() {
      if (document.querySelector('.ag-stats-section')) return;
      const hero = document.querySelector('.template-index main > .shopify-section[id*="hero"], .template-index main > .shopify-section[id*="slideshow"]');
      if (!hero) return;
      const section = document.createElement('section');
      section.className = 'shopify-section ag-stats-section';
      section.innerHTML =
        '<div class="ag-stats">' +
          '<div class="ag-stat"><div class="ag-stat__value" data-target="4.9" data-decimals="1">0</div><div class="ag-stat__label">Customer Rating</div></div>' +
          '<div class="ag-stat"><div class="ag-stat__value" data-target="14">0</div><div class="ag-stat__label">Day Returns</div></div>' +
          '<div class="ag-stat"><div class="ag-stat__value" data-target="48" data-suffix="h">0</div><div class="ag-stat__label">Ship Within</div></div>' +
          '<div class="ag-stat"><div class="ag-stat__value" data-target="100" data-suffix="%">0</div><div class="ag-stat__label">Satisfaction</div></div>' +
        '</div>';
      hero.parentNode.insertBefore(section, hero.nextSibling);

      // Animate count-up when visible
      const statIO = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.target);
          const decimals = el.dataset.decimals ? parseInt(el.dataset.decimals) : 0;
          const suffix = el.dataset.suffix || '';
          let start = null;
          const duration = 1400;
          function tick(ts) {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const val = target * eased;
            el.textContent = val.toFixed(decimals) + suffix;
            if (progress < 1) requestAnimationFrame(tick);
            else el.textContent = target.toFixed(decimals) + suffix;
          }
          requestAnimationFrame(tick);
          statIO.unobserve(el);
        });
      }, { threshold: 0.4 });
      section.querySelectorAll('.ag-stat__value').forEach(function(el) { statIO.observe(el); });
    })();
  }
  /* ====== end v7 JS ====== */

  
  

  // =========================================================
  // INIT
  // =========================================================
  function init() {
    try { initStickyATC(); } catch (e) { console.warn('[AG] sticky ATC:', e); }
    try { initFadeIns(); } catch (e) { console.warn('[AG] fade-ins:', e); }
    try { initLazyLoaded(); } catch (e) { console.warn('[AG] lazy:', e); }
    try { initQtyStepper(); } catch (e) { console.warn('[AG] qty:', e); }
    try { initStickyCartRepair(); } catch (e) { console.warn('[AG] sticky cart repair:', e); }
    try { initStickyCartCustomHandler(); } catch (e) { console.warn('[AG] sticky cart custom:', e); }
    try { initHomepageReactive(); } catch (e) { console.warn('[AG] homepage reactive:', e); }
    try { initReactiveV7(); } catch (e) { console.warn('[AG] reactive v7:', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on resize threshold crossings (mobile ↔ desktop)
  let wasMobile = isMobile();
  window.addEventListener('resize', () => {
    const nowMobile = isMobile();
    if (nowMobile !== wasMobile) {
      wasMobile = nowMobile;
      // If switching to mobile on product page, try to init sticky bar
      if (nowMobile && isProductPage() && !document.querySelector('.ag-mobile-atc')) {
        initStickyATC();
      }
    }
  });
})();

/* ============================================================
   HEADER SCROLL STATE (added 2026-04-14)
   Toggle .is-scrolled on <header> when page scrolled >40px.
   Used by premium-dark.css for frosted-glass effect.
   ============================================================ */
(function () {
  'use strict';
  const header = document.querySelector('header.site-header, .site-header, header[data-section-type="header"]');
  if (!header) return;
  let raf = null;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      if (window.scrollY > 40) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
      raf = null;
    });
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ============================================================
   FOOTER COMPLIANCE PATCH
   - Fixes the broken "/pages/payment-policy-1" menu link (404)
     by rewriting any occurrence to "/pages/payment-policy".
   - Adds a "Contact Information" link to the policy column if
     it's missing, pointing at /policies/contact-information.
   Runs once on DOMContentLoaded. Safe no-op on pages without
   the relevant elements.
   ============================================================ */
(function patchFooterCompliance() {
  'use strict';
  const run = () => {
    try {
      // 1. Rewrite broken payment-policy link
      document.querySelectorAll('a[href="/pages/payment-policy-1"], a[href*="/pages/payment-policy-1"]').forEach(a => {
        a.setAttribute('href', '/pages/payment-policy');
      });

      // 2. Inject Contact Information link into footer if missing
      const hasContactInfo = !!document.querySelector('a[href="/policies/contact-information"], a[href*="/policies/contact-information"]');
      if (!hasContactInfo) {
        // Find the footer policy column (ul containing refund/privacy links)
        const policyList = [...document.querySelectorAll('.site-footer ul, footer ul')].find(ul =>
          ul.querySelector('a[href*="/policies/"]')
        );
        if (policyList) {
          const sample = policyList.querySelector('li') || document.createElement('li');
          const li = document.createElement(sample.tagName === 'LI' ? 'li' : 'div');
          if (sample.className) li.className = sample.className;
          const a = document.createElement('a');
          a.href = '/policies/contact-information';
          a.textContent = 'Contact Information';
          li.appendChild(a);
          policyList.appendChild(li);
        }
      }
    } catch (_) { /* fail quietly */ }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();


/* ====== PDP Polish v2 JS — mark single-option variant selectors + empty text blocks ====== */
(function aquaPdpPolishV2() {
  function markSingleOptionSelectors() {
    document.querySelectorAll('.selector-wrapper.product-form__item').forEach(function(w) {
      var radios = w.querySelectorAll('input[type="radio"]');
      var selects = w.querySelectorAll('select option');
      var optionCount = Math.max(radios.length, selects.length);
      if (optionCount <= 1) {
        w.classList.add('aqua-single-option');
      } else {
        w.classList.remove('aqua-single-option');
      }
    });
  }
  function markEmptyTextBlocks() {
    // Hide .product-label when it has no visible content
    document.querySelectorAll('.product-block.product-title .product-label').forEach(function(el) {
      var hasVisibleChild = Array.prototype.some.call(el.children, function(c) {
        return getComputedStyle(c).display !== 'none' && getComputedStyle(c).visibility !== 'hidden';
      });
      var hasVisibleText = Array.prototype.some.call(el.childNodes, function(n) {
        return n.nodeType === 3 && n.textContent.trim().length > 0;
      });
      if (!hasVisibleChild && !hasVisibleText) {
        el.classList.add('aqua-empty-label');
      } else {
        el.classList.remove('aqua-empty-label');
      }
    });
    // Also hide empty product__text blocks as before
    document.querySelectorAll('.product-single__meta .product-block.product__text, .product-single__meta > p').forEach(function(el) {
      if (!el.textContent.trim() && el.children.length === 0) {
        el.classList.add('aqua-empty-block');
      }
    });
  }
  function run() { markSingleOptionSelectors(); markEmptyTextBlocks(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Re-run when variant changes (some themes re-render the wrapper)
  document.addEventListener('variant:change', run);
  document.addEventListener('product:variant:change', run);
})();
/* ====== end v2 JS ====== */
